import CloseIcon from "@mui/icons-material/Close";
import { Box, Button, Dialog } from "@mui/material";
import { useEffect, useMemo, useState } from "react";

import { useAppSelector } from "../../app/hooks";
import { HelperBotType, HelperBotTypeLabels } from "../../constants/enum/enum";
import { HelperBots } from "../../constants/helper";
import { RankXP } from "../../constants/rank";
import { HelperBotEndInfo } from "../../types/HelperBotEndInfo";
import { convertDateTimeSimple, toUSDFormat } from "../../utils/utils";

import runTimeImg from "../../assets/imgs/crime/clockwise.png";
import loadingArrowImg from "../../assets/imgs/helperbot/loading-arrow.png";
import rankingImg from "../../assets/imgs/helperbot/ranking-badge.png";
import helperImg from "../../assets/imgs/slot/helperbot.png";

import useStyles from "./HelperBotFinishedStyles";

interface HelperBotFinishedPopupProps {
  botEndedInfo: HelperBotEndInfo;
  openModal: boolean;
  setOpenModal: React.Dispatch<React.SetStateAction<boolean>>;
}

const HelperBotFinishedPopup: React.FC<HelperBotFinishedPopupProps> = ({
  botEndedInfo,
  openModal,
  setOpenModal,
}) => {
  const { classes } = useStyles();

  const { myProfile } = useAppSelector((state) => state.profile);

  const [attemptCount, setAttemptCount] = useState(0);

  const {
    userCarBotInfo,
    userCrimeBotInfo,
    userBoozeBotInfo,
    userKSBotInfo,
    userNarcsBotInfo,
    userBulletBotInfo,
  } = useAppSelector((state) => state.game);

  const botAttemptCounts: Record<number, number> = useMemo(
    () => ({
      [HelperBotType.CrimeBot]: userCrimeBotInfo.attemptCount,
      [HelperBotType.GrandTheftAutoBot]: userCarBotInfo.attemptCount,
      [HelperBotType.BoozeSmugglingBot]: userBoozeBotInfo.attemptCount,
      [HelperBotType.ShootingPracticeBot]: userKSBotInfo.attemptCount,
      [HelperBotType.NarcoticsSmugglingBot]: userNarcsBotInfo.attemptCount,
      [HelperBotType.BulletDealerBot]: userBulletBotInfo.attemptCount,
    }),
    [
      userCrimeBotInfo.attemptCount,
      userCarBotInfo.attemptCount,
      userBoozeBotInfo.attemptCount,
      userKSBotInfo.attemptCount,
      userNarcsBotInfo.attemptCount,
      userBulletBotInfo.attemptCount,
    ]
  );

  useEffect(() => {
    setAttemptCount(botAttemptCounts[botEndedInfo.botType] || 0);
  }, [botEndedInfo.botType, botAttemptCounts]);

  const rankPercent = useMemo(() => {
    if (!myProfile.rankNumber) return 0;

    if (botEndedInfo.botType === HelperBotType.ShootingPracticeBot)
      return botEndedInfo.xpEarned / 100 / 100; // 10000% = 1

    const rankXPRange =
      RankXP[myProfile.rankNumber] - RankXP[myProfile.rankNumber - 1];

    return toUSDFormat((botEndedInfo.xpEarned / rankXPRange) * 100, 4);
  }, [botEndedInfo, myProfile]);

  const currentBot =
    HelperBots.find((bot) => bot.id === botEndedInfo.botType) || HelperBots[0];

  const getBotRewardDetails = () => {
    switch (botEndedInfo.botType) {
      case HelperBotType.GrandTheftAutoBot:
        return {
          imgSrc: "/assets/imgs/inventory/carsitem.png",
          label: "Nicked Cars",
          value: toUSDFormat(botEndedInfo.carCount),
        };
      case HelperBotType.BulletDealerBot:
        return {
          imgSrc: "/assets/imgs/inventory/bulletitem.png",
          label: "Earned Bullets:",
          value: toUSDFormat(botEndedInfo.bulletEarned),
        };
      default:
        return {
          imgSrc: "/assets/imgs/inventory/cashitem.png",
          label: "Cash reward:",
          value: toUSDFormat(botEndedInfo.cashEarned),
        };
    }
  };

  const renderDetails = () => {
    const rewardDetails = getBotRewardDetails();

    return (
      <>
        <Box className={classes.details}>
          <Box>
            <Box component={"img"} src={rewardDetails.imgSrc} />
            {rewardDetails.label}
          </Box>
          {rewardDetails.value}
        </Box>

        {botEndedInfo.botType !== HelperBotType.BulletDealerBot && (
          <Box className={classes.details}>
            <Box>
              <Box component={"img"} src={rankingImg} />
              {botEndedInfo.botType === HelperBotType.ShootingPracticeBot
                ? "Kill Skill XP:"
                : "Rank XP:"}
            </Box>
            {toUSDFormat(rankPercent, 4)} %
          </Box>
        )}
      </>
    );
  };

  const handleClose = () => {
    setOpenModal(false);
  };

  return (
    <Dialog open={openModal} onClose={handleClose} className={classes.body}>
      <Box className={classes.modalContent}>
        <Box className={classes.closeIconBody}>
          <Button onClick={handleClose}>
            <CloseIcon htmlColor="gray" />
          </Button>
        </Box>

        <Box className={classes.contentBody}>
          <Box component={"img"} src={helperImg} className={classes.logoImg} />
          <Box className={classes.titleText}>
            {HelperBotTypeLabels[botEndedInfo.botType as HelperBotType]} -{" "}
            <Box component={"span"} color={"green"}>
              Finished
            </Box>
          </Box>

          <Box className={classes.infoPanel}>
            <Box className={classes.details}>
              <Box>
                <Box component={"img"} src={helperImg} />
                Used credits:
              </Box>
              {attemptCount * currentBot.credits}
            </Box>
            <Box className={classes.details}>
              <Box>
                <Box component={"img"} src={loadingArrowImg} />
                Attempts:
              </Box>
              {attemptCount}
            </Box>
            <Box className={classes.details}>
              <Box>
                <Box component={"img"} src={runTimeImg} />
                Run time:
              </Box>
              {convertDateTimeSimple(
                attemptCount * currentBot.attemptDuration * 60
              )}
            </Box>
            {renderDetails()}
          </Box>
        </Box>
      </Box>
    </Dialog>
  );
};

export default HelperBotFinishedPopup;
